import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    BLOCK_LABELS,
    BLOCKS_BY_KIND,
    ContentEdit,
    ContentEditContext,
    blockFrameProps,
    ContentSection,
    ContentSectionFrame,
    SECTION_BACKGROUNDS,
    SECTION_LAYOUTS,
    ToolContentDocument,
    ToolContentKind,
    ToolContentView,
    ToolDetailBlock,
    ToolDetailBlockType,
    ToolDetailBlockView,
} from '@easytools/tool-content';
import { templatesFor } from './sectionTemplates';
import {
    BlockAt, DRAG_MOVE, DRAG_NEW, Target,
    addSection, duplicateBlock, flatIndex, moveBlock, moveSection, parseMove, removeBlock, removeSection, setBlock, setText,
} from './documentOps';

interface Props {
    kind: ToolContentKind;
    doc: ToolContentDocument;
    target: Target | null;
    readOnly?: boolean;
    /** Blocchi da segnalare dopo una generazione, per posizione nel documento: nuovi o cambiati. */
    marks?: Record<number, 'new' | 'changed'>;
    onSelect: (target: Target) => void;
    onChange: (doc: ToolContentDocument, select?: Target) => void;
    onInsert: (at: BlockAt, type: ToolDetailBlockType) => void;
}

type Drop = { at: BlockAt; edge: 'before' | 'after' | 'end' } | null;

const label = <T extends string>(options: { value: T; label: string }[], value: T | undefined, fallback: T) =>
    options.find(o => o.value === (value ?? fallback))?.label ?? '';

/**
 * Anteprima del contenuto come la vede chi lo legge, con sezioni e blocchi modificabili:
 * i testi si scrivono direttamente nella pagina, il resto (icone, elenchi, immagini,
 * stile) nel pannello a destra. Blocchi e sezioni si spostano col trascinamento o con
 * i pulsanti, che restano raggiungibili da tastiera.
 */
const ContentCanvas: React.FC<Props> = ({ kind, doc, target, readOnly, marks, onSelect, onChange, onInsert }) => {
    const [dragging, setDragging] = useState<BlockAt | null>(null);
    const [drop, setDrop] = useState<Drop>(null);

    if (readOnly) {
        return <div className={`tcs-surface tcs-surface--${kind}`}><ToolContentView document={doc} /></div>;
    }

    const isBlock = (at: BlockAt) => target?.kind === 'block' && target.section === at.section && target.index === at.index;
    const isSection = (s: number) => target?.kind === 'section' && target.section === s;

    const endDrag = () => { setDragging(null); setDrop(null); };

    const accepts = (e: React.DragEvent) => e.dataTransfer.types.includes(DRAG_MOVE) || e.dataTransfer.types.includes(DRAG_NEW);

    const dropHere = (e: React.DragEvent, at: BlockAt) => {
        e.preventDefault();
        e.stopPropagation();
        const moved = parseMove(e.dataTransfer.getData(DRAG_MOVE));
        const type = e.dataTransfer.getData(DRAG_NEW) as ToolDetailBlockType;
        endDrag();
        if (moved) {
            const [next, landed] = moveBlock(doc, moved, at);
            onChange(next, { kind: 'block', ...landed });
        } else if (type && BLOCKS_BY_KIND[kind].includes(type)) {
            onInsert(at, type);
        }
    };

    const overBlock = (e: React.DragEvent<HTMLDivElement>, at: BlockAt) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const edge = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
        if (drop?.edge !== edge || drop.at.section !== at.section || drop.at.index !== at.index) setDrop({ at, edge });
    };

    return (
        <div className={`tcs-surface tcs-surface--${kind}`} onClick={() => onSelect({ kind: 'page' })} onDragEnd={endDrag}>
            <div className="etx-td etx-doc tcs-doc">
                {doc.sections.length === 0 && (
                    <EmptySection kind={kind} onInsert={type => onInsert({ section: 0, index: 0 }, type)}
                        active={drop?.edge === 'end'} onDragOver={e => { if (accepts(e)) { e.preventDefault(); setDrop({ at: { section: 0, index: 0 }, edge: 'end' }); } }}
                        onDrop={e => dropHere(e, { section: 0, index: 0 })} />
                )}
                {doc.sections.map((section, s) => (
                    <React.Fragment key={s}>
                        {s > 0 && <AddSection kind={kind} onAdd={section => onChange(addSection(doc, s, section), { kind: 'section', section: s })} />}
                        <div className={`tcs-section${isSection(s) ? ' is-selected' : ''}`}
                            onClick={e => { e.stopPropagation(); onSelect({ kind: 'section', section: s }); }}>
                            <div className="tcs-section__bar" role="toolbar" aria-label={`Sezione ${s + 1}`} onClick={e => e.stopPropagation()}>
                                <button type="button" className="tcs-section__name" onClick={() => onSelect({ kind: 'section', section: s })}>
                                    <i className="bi bi-layout-split" aria-hidden="true" />
                                    Sezione {s + 1}
                                    <span>{label(SECTION_BACKGROUNDS, section.background, 'plain')} · {label(SECTION_LAYOUTS, section.layout, 'stack').toLowerCase()}</span>
                                </button>
                                <button type="button" className="tcs-block__btn" aria-label="Sposta su la sezione" title="Sposta su" disabled={s === 0}
                                    onClick={() => onChange(moveSection(doc, s, s - 1), { kind: 'section', section: s - 1 })}><i className="bi bi-arrow-up" /></button>
                                <button type="button" className="tcs-block__btn" aria-label="Sposta giù la sezione" title="Sposta giù" disabled={s === doc.sections.length - 1}
                                    onClick={() => onChange(moveSection(doc, s, s + 1), { kind: 'section', section: s + 1 })}><i className="bi bi-arrow-down" /></button>
                                <button type="button" className="tcs-block__btn tcs-block__btn--danger" aria-label="Elimina la sezione" title="Elimina la sezione"
                                    onClick={() => {
                                        if (section.blocks.length && !window.confirm(`Eliminare la sezione ${s + 1} con i suoi ${section.blocks.length} blocchi?`)) return;
                                        onChange(removeSection(doc, s), { kind: 'page' });
                                    }}><i className="bi bi-trash" /></button>
                            </div>
                            <ContentSectionFrame section={section}>
                                {section.blocks.map((block, index) => {
                                    const at = { section: s, index };
                                    const flat = flatIndex(doc, at);
                                    const dropEdge = drop && drop.edge !== 'end' && drop.at.section === s && drop.at.index === index ? drop.edge : null;
                                    return (
                                        <EditableBlock
                                            key={index}
                                            block={block}
                                            selected={isBlock(at)}
                                            dragging={dragging?.section === s && dragging.index === index}
                                            dropEdge={dropEdge}
                                            mark={marks?.[flat]}
                                            first={index === 0 && s === 0}
                                            last={index === section.blocks.length - 1 && s === doc.sections.length - 1}
                                            onSelect={() => onSelect({ kind: 'block', ...at })}
                                            onBlock={next => onChange(setBlock(doc, at, next))}
                                            onMove={dir => {
                                                const to = dir < 0
                                                    ? (index > 0 ? { section: s, index: index - 1 } : { section: s - 1, index: doc.sections[s - 1].blocks.length })
                                                    : (index < section.blocks.length - 1 ? { section: s, index: index + 2 } : { section: s + 1, index: 0 });
                                                const [next, landed] = moveBlock(doc, at, to);
                                                onChange(next, { kind: 'block', ...landed });
                                            }}
                                            onDuplicate={() => { const [next, landed] = duplicateBlock(doc, at); onChange(next, { kind: 'block', ...landed }); }}
                                            onRemove={() => onChange(removeBlock(doc, at), { kind: 'section', section: s })}
                                            onDragStart={e => { setDragging(at); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData(DRAG_MOVE, `${s}:${index}`); }}
                                            onDragOver={e => overBlock(e, at)}
                                            onDrop={e => dropHere(e, drop?.edge === 'after' ? { section: s, index: index + 1 } : at)}
                                        />
                                    );
                                })}
                            </ContentSectionFrame>
                            <SectionEnd kind={kind} empty={!section.blocks.length}
                                active={drop?.edge === 'end' && drop.at.section === s}
                                onInsert={type => onInsert({ section: s, index: section.blocks.length }, type)}
                                onDragOver={e => { if (accepts(e)) { e.preventDefault(); e.stopPropagation(); setDrop({ at: { section: s, index: section.blocks.length }, edge: 'end' }); } }}
                                onDrop={e => dropHere(e, { section: s, index: section.blocks.length })} />
                        </div>
                    </React.Fragment>
                ))}
                {doc.sections.length > 0 && <AddSection kind={kind} onAdd={section => onChange(addSection(doc, doc.sections.length, section), { kind: 'section', section: doc.sections.length })} />}
            </div>
        </div>
    );
};

const EditableBlock: React.FC<{
    block: ToolDetailBlock;
    selected: boolean;
    dragging: boolean;
    dropEdge: 'before' | 'after' | null;
    mark?: 'new' | 'changed';
    first: boolean;
    last: boolean;
    onSelect: () => void;
    onBlock: (block: ToolDetailBlock) => void;
    onMove: (dir: -1 | 1) => void;
    onDuplicate: () => void;
    onRemove: () => void;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
    onDrop: (e: React.DragEvent) => void;
}> = ({ block, selected, dragging, dropEdge, mark, first, last, onSelect, onBlock, onMove, onDuplicate, onRemove, onDragStart, onDragOver, onDrop }) => {
    const frame = useRef<HTMLDivElement>(null);
    const appearance = blockFrameProps(block);
    // Il blocco è sempre quello dell'ultimo rendering: un testo confermato all'uscita dal campo usa i dati aggiornati.
    const latest = useRef(block);
    latest.current = block;
    const edit = useMemo<ContentEdit>(() => ({ onText: (path, value) => onBlock(setText(latest.current, path, value)) }), [onBlock]);

    return (
        <div
            ref={frame}
            id={appearance.id}
            className={`tcs-block ${appearance.className}${selected ? ' is-selected' : ''}${dragging ? ' is-dragging' : ''}${mark ? ` is-${mark}` : ''}${dropEdge ? ` is-drop-${dropEdge}` : ''}`}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onClick={e => { e.stopPropagation(); onSelect(); }}
        >
            <div className="tcs-block__bar" role="toolbar" aria-label={BLOCK_LABELS[block.type]} onClick={e => e.stopPropagation()}>
                <span className="tcs-block__grip" draggable title="Trascina per spostare" aria-hidden="true"
                    onDragStart={e => { if (frame.current) e.dataTransfer.setDragImage(frame.current, 24, 16); onDragStart(e); }}>
                    <i className="bi bi-grip-vertical" />
                </span>
                <button type="button" className="tcs-block__type" onClick={onSelect}>{BLOCK_LABELS[block.type]}</button>
                <button type="button" className="tcs-block__btn" aria-label="Sposta su" title="Sposta su" disabled={first} onClick={() => onMove(-1)}><i className="bi bi-arrow-up" /></button>
                <button type="button" className="tcs-block__btn" aria-label="Sposta giù" title="Sposta giù" disabled={last} onClick={() => onMove(1)}><i className="bi bi-arrow-down" /></button>
                <button type="button" className="tcs-block__btn" aria-label="Duplica" title="Duplica" onClick={onDuplicate}><i className="bi bi-copy" /></button>
                <button type="button" className="tcs-block__btn tcs-block__btn--danger" aria-label="Elimina" title="Elimina" onClick={onRemove}><i className="bi bi-trash" /></button>
            </div>
            {block.appearance?.hideOn && (
                <span className="tcs-block__hidden" title="Lo stile del blocco lo nasconde su questo dispositivo">
                    <i className="bi bi-eye-slash" aria-hidden="true" />{block.appearance.hideOn === 'phone' ? 'Nascosto su telefono' : 'Solo su telefono'}
                </span>
            )}
            {mark && <span className="tcs-block__mark">{mark === 'new' ? 'Nuovo' : 'Cambiato'}</span>}
            <div className="tcs-block__body">
                <ContentEditContext.Provider value={edit}>
                    <ToolDetailBlockView block={block} />
                </ContentEditContext.Provider>
                {block.type === 'pageBreak' && <p className="tcs-block__blank">Interruzione di pagina nel PDF</p>}
            </div>
        </div>
    );
};

/** In fondo a ogni sezione: aggiungi un blocco, o trascinalo qui. */
const SectionEnd: React.FC<{
    kind: ToolContentKind;
    empty: boolean;
    active: boolean;
    onInsert: (type: ToolDetailBlockType) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
}> = ({ kind, empty, active, onInsert, onDragOver, onDrop }) => (
    <div className={`tcs-section__end${empty ? ' is-empty' : ''}${active ? ' is-drop' : ''}`} onDragOver={onDragOver} onDrop={onDrop}
        onClick={e => e.stopPropagation()}>
        {empty && <span>Sezione vuota: trascina qui un blocco dalla libreria, oppure</span>}
        <InsertMenu kind={kind} onInsert={onInsert} label={empty ? 'scegline uno' : 'Aggiungi blocco'} />
    </div>
);

const EmptySection: React.FC<{
    kind: ToolContentKind;
    active: boolean;
    onInsert: (type: ToolDetailBlockType) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
}> = props => (
    <div className="tcs-section">
        <SectionEnd {...props} empty />
    </div>
);

/** Tra due sezioni: una sezione vuota o una delle sezioni pronte. */
const AddSection: React.FC<{ kind: ToolContentKind; onAdd: (section?: ContentSection) => void }> = ({ kind, onAdd }) => {
    const [open, setOpen] = useState(false);
    const menu = useRef<HTMLDivElement>(null);
    useDismiss(open, menu, () => setOpen(false));
    return (
        <div ref={menu} className={`tcs-add-section${open ? ' is-open' : ''}`} onClick={e => e.stopPropagation()}>
            <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)}><i className="bi bi-plus-lg" aria-hidden="true" /> Nuova sezione</button>
            {open && (
                <div className="tcs-insert__menu tcs-add-section__menu" role="menu">
                    <button type="button" role="menuitem" onClick={() => { onAdd(); setOpen(false); }}><strong>Sezione vuota</strong></button>
                    {templatesFor(kind).map(t => (
                        <button key={t.id} type="button" role="menuitem" title={t.hint} onClick={() => { onAdd(t.build()); setOpen(false); }}>
                            <i className={`bi ${t.icon} me-1`} aria-hidden="true" />{t.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/** Chiude un menu con Esc o con un clic fuori. */
function useDismiss(open: boolean, ref: React.RefObject<HTMLElement | null>, close: () => void) {
    useEffect(() => {
        if (!open) return;
        const onEvent = (e: MouseEvent | KeyboardEvent) => {
            if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) close();
        };
        document.addEventListener('mousedown', onEvent);
        document.addEventListener('keydown', onEvent);
        return () => { document.removeEventListener('mousedown', onEvent); document.removeEventListener('keydown', onEvent); };
    }, [open, ref, close]);
}

const InsertMenu: React.FC<{ kind: ToolContentKind; label: string; onInsert: (type: ToolDetailBlockType) => void }> = ({ kind, label: text, onInsert }) => {
    const [open, setOpen] = useState(false);
    const menu = useRef<HTMLDivElement>(null);

    useDismiss(open, menu, () => setOpen(false));

    return (
        <div ref={menu} className={`tcs-insert${open ? ' is-open' : ''}`}>
            <button type="button" className="tcs-insert__btn" aria-expanded={open} onClick={() => setOpen(o => !o)}>
                <i className="bi bi-plus-lg" /> {text}
            </button>
            {open && (
                <div className="tcs-insert__menu" role="menu">
                    {BLOCKS_BY_KIND[kind].map(type => (
                        <button key={type} type="button" role="menuitem" onClick={() => { onInsert(type); setOpen(false); }}>
                            {BLOCK_LABELS[type]}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ContentCanvas;
